import type { LeaveBalanceRow } from "./LeaveInsightCards.js";
import { useLeaveTypeConfig } from "../context/LeaveTypeConfigContext.js";
import { availableDays } from "../lib/leaveBalance.js";
import { Palmtree, Thermometer, Sun, RefreshCw, Home, Baby, Users, ClipboardList, CheckCircle2, Heart, Flower2, Wallet } from "lucide-react";

const TYPE_META: Record<string, { icon: typeof Palmtree }> = {
  ANNUAL:    { icon: Palmtree },
  SICK:      { icon: Thermometer },
  CASUAL:    { icon: Sun },
  COMP_OFF:  { icon: RefreshCw },
  WFH:       { icon: Home },
  MATERNITY: { icon: Baby },
  PATERNITY: { icon: Users },
  WEDDING:     { icon: Heart },
  BEREAVEMENT: { icon: Flower2 },
  LOP:         { icon: Wallet },
};

interface LeaveTypeOption {
  value: string;
  displayName: string;
}

interface Props {
  types:    LeaveTypeOption[];
  balance:  LeaveBalanceRow[];
  value:    string;
  onChange: (type: string) => void;
  disabled?: boolean;
}

export function LeaveTypePicker({ types, balance, value, onChange, disabled }: Props) {
  const config     = useLeaveTypeConfig();
  const balanceMap = Object.fromEntries(balance.map((b) => [b.leaveType, b]));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {types.map((t) => {
        const meta        = TYPE_META[t.value] ?? { icon: ClipboardList };
        const Icon        = meta.icon;
        const bal         = balanceMap[t.value];
        const isSelected  = value === t.value;
        const isWfhType   = t.value === "WFH";
        const isUnlimited = config[t.value]?.trackBalance === false && !isWfhType;

        const available = bal ? availableDays(bal) : null;

        const balanceLabel = isWfhType
          ? "1 per quarter"
          : isUnlimited
            ? "Unlimited"
            : available !== null
              ? `${available % 1 === 0 ? available : available.toFixed(2)} days`
              : "No balance";

        return (
          <button
            key={t.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(t.value)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 14px",
              background: isSelected ? "var(--color-brand-50, #eff6ff)" : "var(--color-surface-0)",
              border: isSelected ? "2px solid var(--color-brand-500)" : "1.5px solid var(--color-surface-200)",
              borderRadius: 10,
              cursor: disabled ? "not-allowed" : "pointer", textAlign: "left",
              opacity: disabled && !isSelected ? 0.5 : 1,
              transition: "border-color 0.1s, background 0.1s",
            }}
          >
            <Icon size={20} style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t.displayName}
              </div>
              <div style={{ fontSize: 11, color: isUnlimited ? "var(--color-surface-400)" : (available !== null && available <= 0 ? "var(--color-error)" : "var(--color-surface-500)") }}>
                {balanceLabel}
              </div>
            </div>
            {isSelected && (
              <span style={{ marginLeft: "auto", flexShrink: 0, display: "inline-flex", color: "var(--color-brand-500)" }}><CheckCircle2 size={14} /></span>
            )}
          </button>
        );
      })}
    </div>
  );
}
